import { sql } from "drizzle-orm";
import { boolean, decimal, index, integer, jsonb, pgTable, timestamp, varchar } from "drizzle-orm/pg-core";

export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  passwordHash: varchar("password_hash"),
  authProvider: varchar("auth_provider").default("email"),
  providerId: varchar("provider_id"),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  referralCode: varchar("referral_code").unique(),
  referredByUserId: varchar("referred_by_user_id"),
  trialStartAt: timestamp("trial_start_at"),
  trialEndAt: timestamp("trial_end_at"),
  trialStatus: varchar("trial_status").default("none"),
  subscriptionStatus: varchar("subscription_status").default("inactive"),
  subscriptionTier: varchar("subscription_tier"),
  stripeCustomerId: varchar("stripe_customer_id"),
  stripeSubscriptionId: varchar("stripe_subscription_id"),
  referralDiscountApplied: boolean("referral_discount_applied").default(false),
  termsAcceptedAt: timestamp("terms_accepted_at"),
  lastSeenAt: timestamp("last_seen_at"),
  market: varchar("market").default("uk"), // 'uk' | 'au' | 'ca'
  // Canada-only: user-entered combined sales tax rate (GST + PST/HST/QST varies by province).
  // Stored as percentage (e.g. "13.00" for 13%). Null/0 for UK and AU users (those use fixed rates).
  customTaxRate: decimal("custom_tax_rate", { precision: 5, scale: 2 }),
  // StructSpectrum partnership referral fields
  partnerSource: varchar("partner_source"),
  partnerCode: varchar("partner_code").unique(),
  partnerCodeGeneratedAt: timestamp("partner_code_generated_at"),
  partnerRewardClaimed: boolean("partner_reward_claimed").default(false),
  partnerRewardClaimedAt: timestamp("partner_reward_claimed_at"),
  // Referral link slug fields (custom shareable URL e.g. /skyhouse-design)
  referralSlug: varchar("referral_slug").unique(),
  referralSlugCreatedAt: timestamp("referral_slug_created_at"),
  referralCompanyName: varchar("referral_company_name"),
  // Admin-enforced quota flag. When true and user is on trial (not active subscriber),
  // all quote generation is blocked immediately without needing a count query.
  // Used to block users who create multiple accounts to bypass the shared trial quote cap.
  quotaLimitEnforced: boolean("quota_limit_enforced").notNull().default(false),
  // Admin-only notes for case management (e.g. "emailed 2026-06-07, awaiting reply").
  adminNotes: varchar("admin_notes", { length: 2000 }),
  // Timestamp of the last re-engagement email sent by an admin. Null if never sent.
  reengagementEmailSentAt: timestamp("reengagement_email_sent_at"),
  // Timestamp of the last first-quote nudge email sent by an admin. Null if never sent.
  firstQuoteNudgeEmailSentAt: timestamp("first_quote_nudge_email_sent_at"),
  // Non-expiring bonus Auto Quote credits granted by admin. Monthly allowance is consumed first;
  // bonus credits are used only when the monthly allowance is exhausted. Carries over on reset.
  bonusQuotaCredits: integer("bonus_quota_credits").notNull().default(0),
  // Stripe billing cycle anchor: start of the current billing period (current_period_start from
  // Stripe webhook). Used to count quotes against the user's personal billing cycle rather than
  // the calendar month. Updated on every subscription.created / subscription.updated event.
  // Null for trial users and legacy subscribers (falls back to calendar month start).
  subscriptionPeriodStart: timestamp("subscription_period_start"),
  // Account deletion (multi-step, safe deletion — see accountDeletionRequests
  // in schema.ts). Set once an admin confirms a deletion request; from that
  // point the account is anonymised (PII scrubbed) but the row is retained
  // (financial/subscription history kept for 6 years). Non-null anonymizedAt
  // must permanently block login/auth for this account.
  anonymizedAt: timestamp("anonymized_at"),
  // Whether the user has opted out of feature-update announcement emails.
  // Set via the unsubscribe link in those emails (M039). Blocks sends from
  // the Feature Updates admin page; honored in getUsersForFeatureUpdateSend.
  emailUpdatesOptOut: boolean("email_updates_opt_out").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

export type UserProfile = User & {
  trialQuotaExceeded: boolean;
  monthlyQuotaExceeded: boolean;
  trialQuotesUsed?: number;
  monthlyQuotesUsed?: number;
  monthlyQuotaLimit?: number;
  bonusQuotaCredits?: number;
  monthlyQuotaResetAt?: string;
  pendingDeletionRequest?: { id: string; requestedAt: string } | null;
};
